import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import { ProjectList } from "../helpers/ProjectList";
import GitHubIcon from "@mui/icons-material/GitHub";
import LanguageIcon from "@mui/icons-material/Language";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import ArticleIcon from "@mui/icons-material/Article";
import "../styles/ProjectDisplay.css";

/* ── Category config ── */
const CAT_META = {
  software: { label: "Software",  cls: "cat-software" },
  cyber:    { label: "Cyber Sec", cls: "cat-cyber"    },
  devops:   { label: "DevOps",    cls: "cat-devops"   },
};

/* ── Seeded date (matches Projects.js seedDate) ── */
const SEED_DATES = [
  "Mar 2019","Aug 2019","Jan 2020","Jun 2020","Oct 2020",
  "Feb 2021","Jul 2021","Nov 2021","Apr 2022","Sep 2022",
  "Jan 2023","May 2023","Oct 2023","Feb 2024","Jul 2024",
  "Nov 2024","Mar 2025","Jun 2025",
];

/* ── Placeholder tech stacks ── */
const TECH_STACKS = {
  software: ["React","Node.js","TypeScript","PostgreSQL","REST API"],
  cyber:    ["Python","Kali Linux","Burp Suite","Wireshark","Metasploit"],
  devops:   ["Docker","Kubernetes","Terraform","GitHub Actions","AWS"],
};

function TechPill({ label }) {
  return (
    <span className="pd-tech-tag">
      <span className="pd-tech-icon-slot" aria-hidden="true" />
      {label}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────── */
function ProjectDisplay() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const project  = ProjectList[id];

  if (!project) {
    return (
      <div className="pd-page">
        <div className="pd-shell">
          <p style={{ color: "#94a3b8", fontFamily: "var(--font-mono)", fontSize: "0.8rem" }}>
            Project not found.
          </p>
          <button className="pd-back-btn" onClick={() => navigate("/projects")}>
            <ArrowBackIcon style={{ fontSize: 15 }} /> All Projects
          </button>
        </div>
      </div>
    );
  }

  return <ProjectInner id={id} project={project} navigate={navigate} />;
}

function ProjectInner({ id, project, navigate }) {
  useEffect(() => {
    const script = document.createElement("script");
    script.type  = "application/ld+json";
    script.text  = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: project.name,
      author: { "@type": "Person", name: "Biko Sulu" },
      url: project.live || project.github,
      applicationCategory:
        project.category === "cyber"   ? "SecurityApplication"  :
        project.category === "devops"  ? "DeveloperApplication" :
        "WebApplication",
    });
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, [project]);

  const idx          = parseInt(id);
  const cat          = CAT_META[project.category] || { label: project.category, cls: "cat-software" };
  const date         = SEED_DATES[idx % SEED_DATES.length];
  const stack        = TECH_STACKS[project.category] || TECH_STACKS.software;
  const skillsArr    = Array.isArray(project.skills) ? project.skills : [project.skills];

  return (
    <div className="pd-page">
      <Helmet>
        <title>{project.name} | Biko Projects</title>
        <meta name="description" content={`${project.name} — ${project.description} Built by Biko Sulu.`} />
        <meta property="og:title"       content={`${project.name} | Biko Sulu`} />
        <meta property="og:description" content={project.description} />
        <meta property="og:url"         content={`https://bikosulu.com/project/${id}`} />
        {project.image && <meta property="og:image" content={project.image} />}
      </Helmet>

      <div className="pd-shell">

        {/* ── Page nav toggle ── */}
        <nav className="page-nav-toggle">
          <button className="page-nav-btn" onClick={() => navigate("/")}>
            <HomeIcon style={{ fontSize: 14 }} /> Home
          </button>
          <span className="page-nav-sep">/</span>
          <button className="page-nav-btn" onClick={() => navigate("/projects")}>
            <ArrowBackIcon style={{ fontSize: 14 }} /> Projects
          </button>
          <span className="page-nav-sep">/</span>
          <span className="page-nav-current">{project.name}</span>
          <span className="page-nav-sep">/</span>
          <button className="page-nav-btn" onClick={() => navigate("/blogs")}>
            <ArticleIcon style={{ fontSize: 14 }} /> Blog
          </button>
        </nav>

        {/* ── Header ── */}
        <header className="pd-header">
          <div className="pd-header-meta">
            <span className="pd-project-id">SEC_OBJ_{String(idx + 1).padStart(2, "0")}</span>
            <span className={`pd-cat-badge ${cat.cls}`}>{cat.label}</span>
            <span className="pd-date">{date}</span>
          </div>
          <h1 className="pd-title">{project.name}</h1>
          <p className="pd-hero-desc">{project.description}</p>
        </header>

        {/* ── Two-column body ── */}
        <div className="pd-body">

          {/* ── LEFT: image + links ── */}
          <div className="pd-left">

            <div className="pd-image-wrap">
              {project.image ? (
                <img src={project.image} alt={project.name} />
              ) : (
                <div className="pd-img-placeholder">
                  <span className="pd-placeholder-text">
                    {project.name.slice(0, 2).toUpperCase()}
                  </span>
                  <div className="pd-placeholder-grid" />
                </div>
              )}
            </div>

            {/* Tech stack pills */}
            <div className="pd-stack-section">
              <span className="pd-stack-label">Tech Stack</span>
              <div className="pd-tech-row">
                {stack.map((t) => <TechPill key={t} label={t} />)}
              </div>
            </div>

            {/* Action buttons */}
            <div className="pd-link-row">
              {project.github && (
                <a href={project.github} target="_blank" rel="noreferrer" className="pd-btn pd-btn-outline">
                  <GitHubIcon style={{ fontSize: 16 }} />
                  View on GitHub
                </a>
              )}
              {project.live && (
                <a href={project.live} target="_blank" rel="noreferrer" className="pd-btn pd-btn-solid">
                  <LanguageIcon style={{ fontSize: 16 }} />
                  Live Demo
                </a>
              )}
            </div>
          </div>

          {/* ── RIGHT: detail sections ── */}
          <div className="pd-right">

            {skillsArr.length > 0 && (
              <div className="pd-info-section">
                <h3>Core Competencies</h3>
                <div className="pd-skills-wrap">
                  {skillsArr.map((s, i) => (
                    <span key={i} className="pd-skill-chip">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {project.problem && (
              <div className="pd-info-section">
                <h3>Problem</h3>
                <p>{project.problem}</p>
              </div>
            )}

            {project.approach && (
              <div className="pd-info-section">
                <h3>Approach</h3>
                <p>{project.approach}</p>
              </div>
            )}

            {project.highlights && project.highlights.length > 0 && (
              <div className="pd-info-section">
                <h3>Implementation</h3>
                <ul className="pd-case-list">
                  {project.highlights.map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              </div>
            )}

            {project.impact && (
              <div className="pd-info-section">
                <h3>Impact</h3>
                <p>{project.impact}</p>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectDisplay;